import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { AimaLogo } from "@/components/AimaLogo";
import { Reveal, RevealStagger, RevealItem } from "@/components/Reveal";
import { SignalMesh } from "@/components/SignalMesh";
import { ParallaxGlow } from "@/components/ParallaxGlow";
import { AICore } from "@/components/AICore";
import { MarketTicker } from "@/components/MarketTicker";
import { VisionSection } from "@/components/VisionSection";
import { LayerShowcase } from "@/components/LayerShowcase";
import { AmbientBackground } from "@/components/AmbientBackground";
import { RestrictedTokenSale } from "@/components/RestrictedTokenSale";
import { TeamShowcase } from "@/components/TeamShowcase";
import { SmoothScroll } from "@/components/SmoothScroll";
import { CinematicVignette } from "@/components/CinematicVignette";
import { LanguageSelector } from "@/components/LanguageSelector";
import { WhitepaperButton, WhitepaperLink } from "@/components/WhitepaperButton";
import { SocialLinks } from "@/components/SocialLinks";
import { getLegalPage } from "@/data/legal";
import { useTranslation } from "react-i18next";
import {
    ArrowUpRight,
    ArrowRight,
    Activity,
    LineChart,
    ShieldAlert,
    Waves,
    Network,
    Compass,
    User,
    Sparkles,
    Layers as LayersIcon,
    BrainCircuit,
    Lock,
    CircleDot,
    Menu,
    X,
} from "lucide-react";

/* =============================================================== */
/*  NAV                                                            */
/* =============================================================== */
const Nav = ({ onNavigate }) => {
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { href: "#solution", label: t("nav.platform") },
        { href: "#layers", label: t("nav.intelligence") },
        { href: "#why", label: t("nav.architecture") },
        { href: "#roadmap", label: t("nav.roadmap") },
        { href: "#token-sale", label: t("nav.token") },
    ];

    return (
        <motion.header
            data-testid="site-header"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className={`fixed top-0 inset-x-0 z-40 transition-[background,border-color,backdrop-filter] duration-500 ${
                scrolled || mobileOpen
                    ? "backdrop-blur-xl bg-black/80 border-b border-white/[0.08]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-x max-w-[1400px] mx-auto flex items-center justify-between h-[72px] md:h-20 gap-4">
                <a href="#top" data-testid="nav-logo-link" className="flex items-center shrink-0">
                    <AimaLogo variant="horizontal" height={38} />
                </a>
                <nav className="hidden md:flex items-center gap-8 lg:gap-11">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="font-plex text-[14px] font-medium text-white/75 hover:text-white transition-colors whitespace-nowrap"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <div className="hidden md:block">
                        <LanguageSelector />
                    </div>
                    <button
                        onClick={() => onNavigate("platform")}
                        data-testid="nav-cta-button"
                        className="group hidden sm:inline-flex items-center gap-2 h-10 px-4 md:px-5 bg-white text-black font-plex text-[13px] font-medium hover:bg-white/90 transition-colors"
                    >
                        {t("nav.cta")}
                        <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.8} />
                    </button>
                    <button
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                        aria-expanded={mobileOpen}
                        className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-white/10 text-white/80 hover:text-white hover:border-white/25 transition-colors"
                    >
                        {mobileOpen ? <X className="w-5 h-5" strokeWidth={1.8} /> : <Menu className="w-5 h-5" strokeWidth={1.8} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu panel */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="mobile-menu-panel"
                        className="md:hidden overflow-hidden border-t border-white/[0.06] bg-black/80 backdrop-blur-xl"
                    >
                        <div className="container-x max-w-[1400px] mx-auto py-6 flex flex-col gap-5">
                            <nav className="flex flex-col">
                                {links.map((l) => (
                                    <a
                                        key={l.href}
                                        href={l.href}
                                        onClick={() => setMobileOpen(false)}
                                        data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                        className="font-plex text-[15px] text-white/70 hover:text-white transition-colors py-3 border-b border-white/[0.05]"
                                    >
                                        {l.label}
                                    </a>
                                ))}
                            </nav>
                            <div className="flex items-center justify-between gap-4">
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
                                    {t("nav.language")}
                                </span>
                                <LanguageSelector variant="mobile" />
                            </div>
                            <button
                                onClick={() => { setMobileOpen(false); onNavigate("platform"); }}
                                data-testid="mobile-nav-cta"
                                className="group inline-flex items-center justify-center gap-2 h-12 bg-white text-black font-plex text-sm font-medium hover:bg-white/90 transition-colors"
                            >
                                {t("nav.cta")}
                                <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

/* =============================================================== */
/*  HERO                                                           */
/* =============================================================== */
const Hero = ({ onNavigate }) => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const meshY = useTransform(scrollYProgress, [0, 1], [0, 200]);
    const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.3]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative overflow-hidden pt-36 md:pt-48 pb-28 md:pb-44 min-h-[92vh] grain"
        >
            {/* Signal mesh backdrop */}
            <motion.div
                style={{ y: meshY, opacity: 0.85 }}
                className="absolute inset-0 pointer-events-none [mask-image:radial-gradient(ellipse_85%_75%_at_50%_35%,#000_40%,transparent_85%)]"
            >
                <SignalMesh className="w-full h-full" />
            </motion.div>

            {/* Grid lines */}
            <div className="absolute inset-0 grid-lines-bg opacity-[0.45] pointer-events-none [mask-image:radial-gradient(ellipse_70%_60%_at_50%_30%,#000_40%,transparent_80%)]" />

            {/* Parallax glows */}
            <ParallaxGlow className="-top-40 -left-40" color="violet" size={720} intensity={0.9} />
            <ParallaxGlow className="top-20 -right-60" color="cyan" size={600} intensity={0.7} parallaxStrength={60} />

            <motion.div
                style={{ y: heroY, opacity: heroOpacity }}
                className="relative container-x max-w-[1400px] mx-auto"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    <div className="lg:col-span-7">
                        {/* Status chip */}
                        <Reveal className="flex items-center gap-3 mb-10" y={12} duration={0.7}>
                            <span className="inline-flex items-center gap-2.5 h-8 px-3 border border-white/10 bg-white/[0.02] backdrop-blur-sm">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-pulse-soft absolute inline-flex h-full w-full rounded-full bg-cyan/80" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan" />
                                </span>
                                <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/75">
                                    {t("hero.badge")}
                                </span>
                            </span>
                        </Reveal>

                        {/* Headline (word-staggered) */}
                        <HeroHeadline />

                        {/* Sub */}
                        <Reveal
                            delay={0.55}
                            className="mt-8 md:mt-10 max-w-2xl font-plex text-base md:text-lg text-white/60 leading-relaxed"
                        >
                            {t("hero.sub")}
                        </Reveal>

                        {/* CTAs */}
                        <Reveal
                            delay={0.75}
                            className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start gap-4"
                        >
                            <button
                                onClick={() => onNavigate("platform")}
                                data-testid="hero-explore-button"
                                className="group relative inline-flex items-center gap-3 h-14 px-7 bg-white text-black font-plex text-sm font-medium hover:bg-white/90 transition-colors overflow-hidden"
                            >
                                <span className="relative z-10">{t("hero.cta_primary")}</span>
                                <ArrowRight className="relative z-10 w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.7} />
                            </button>
                            <WhitepaperButton testId="hero-whitepaper-button" size="lg" />
                        </Reveal>
                    </div>

                    {/* AI Core visual (right) */}
                    <Reveal
                        delay={0.5}
                        y={20}
                        className="lg:col-span-5 flex items-center justify-center"
                    >
                        <div className="relative w-full max-w-[520px] aspect-square">
                            <AICore size={520} className="!w-full !h-full" />
                        </div>
                    </Reveal>
                </div>

                {/* Value strip — flowing intelligence line */}
                <RevealStagger
                    initialDelay={1}
                    gap={0.12}
                    className="mt-20 md:mt-24 relative border-t border-white/[0.08] pt-10"
                >
                    {/* connecting signal line */}
                    <motion.div
                        aria-hidden="true"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 1.1 }}
                        className="hidden md:block absolute top-0 left-0 right-0 h-px origin-left bg-gradient-to-r from-transparent via-cyan/50 to-transparent"
                    />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
                        {[
                            t("hero.value.consensus"),
                            t("hero.value.explainability"),
                            t("hero.value.probabilistic"),
                            t("hero.value.platform"),
                        ].map((v, i) => (
                            <RevealItem key={i}>
                                <div className="group relative">
                                    <span className="absolute -top-[41px] left-0 w-2 h-2 rounded-full bg-cyan/70 group-hover:bg-cyan group-hover:shadow-[0_0_12px_rgba(0,229,255,0.7)] transition-all" />
                                    <div className="font-cabinet text-white text-[17px] md:text-[19px] tracking-tight leading-[1.2] group-hover:text-white transition-colors">
                                        {v}
                                    </div>
                                </div>
                            </RevealItem>
                        ))}
                    </div>
                </RevealStagger>
            </motion.div>
        </section>
    );
};

const HeroHeadline = () => {
    const { t } = useTranslation();
    const line1 = t("hero.h1_line1").split(/\s+/);
    const line2 = t("hero.h1_line2").split(/\s+/);

    const variants = {
        hidden: { opacity: 0, y: 30, filter: "blur(10px)" },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.08 },
        }),
    };

    return (
        <h1 className="h-display text-[44px] sm:text-6xl md:text-7xl lg:text-[96px] xl:text-[112px] text-white max-w-[16ch] leading-[0.92]">
            <div className="flex flex-wrap gap-x-3 md:gap-x-5">
                {line1.map((w, i) => (
                    <motion.span
                        key={w + i}
                        custom={i}
                        variants={variants}
                        initial="hidden"
                        animate="visible"
                        className="inline-block text-gradient-soft"
                    >
                        {w}
                    </motion.span>
                ))}
            </div>
            <div className="flex flex-wrap gap-x-3 md:gap-x-5">
                {line2.map((w, i) => (
                    <motion.span
                        key={w + i}
                        custom={i + line1.length}
                        variants={variants}
                        initial="hidden"
                        animate="visible"
                        className="inline-block text-gradient-accent"
                    >
                        {w}
                    </motion.span>
                ))}
            </div>
        </h1>
    );
};

/* =============================================================== */
/*  PROBLEM                                                        */
/* =============================================================== */
const Problem = () => {
    const { t } = useTranslation();
    const cards = t("landing.problem.cards", { returnObjects: true });
    const cardIcons = [Waves, Activity, Lock];
    return (
    <section id="problem" data-testid="problem-section" className="relative section-y overflow-hidden">
        {/* Full-cover cinematic video background */}
        <div aria-hidden="true" className="absolute inset-0 z-0 pointer-events-none">
            <video
                className="absolute inset-0 w-full h-full object-cover"
                src="/media/chart-animation.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
            />
            {/* Readability mask — keeps text effortlessly legible */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(to right, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.35) 75%, rgba(0,0,0,0.45) 100%)",
                }}
            />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 25%, rgba(0,0,0,0.15) 75%, rgba(0,0,0,0.65) 100%)",
                }}
            />
        </div>
        <AmbientBackground variant="aurora" opacity={0.35} />
        <div className="relative z-10 container-x max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
                <Reveal className="md:col-span-7">
                    <h2 className="h-display text-4xl md:text-5xl lg:text-[64px] text-white leading-[0.95]">
                        {t("landing.problem.l1a")} <span className="text-gradient-accent">{t("landing.problem.l1b")}</span>
                        <br />
                        {t("landing.problem.l2a")} <span className="text-gradient-accent">{t("landing.problem.l2b")}</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.1} className="md:col-span-5">
                    <p className="font-plex text-white/80 text-lg md:text-[19px] leading-[1.55] max-w-[46ch]">
                        {t("landing.problem.p1")}
                    </p>
                </Reveal>
            </div>

            <RevealStagger className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" initialDelay={0.15}>
                {cards.map((it, i) => {
                    const Icon = cardIcons[i];
                    return (
                    <RevealItem key={it.t}>
                        <motion.div
                            whileHover={{ y: -4 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="group relative h-full border border-white/[0.08] bg-graphite p-8 md:p-10 overflow-hidden"
                        >
                            <motion.div
                                aria-hidden="true"
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                style={{
                                    background: `radial-gradient(ellipse at ${i === 0 ? "top left" : i === 1 ? "top" : "top right"}, rgba(0,229,255,0.10), transparent 65%)`,
                                }}
                            />
                            <motion.span
                                aria-hidden="true"
                                initial={{ scaleX: 0 }}
                                whileInView={{ scaleX: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.1 }}
                                className="absolute left-0 top-0 h-px w-full origin-left bg-gradient-to-r from-cyan/60 via-cyan/30 to-transparent"
                            />
                            <div className="relative inline-flex items-center justify-center w-11 h-11 border border-white/[0.1] bg-white/[0.02] group-hover:border-cyan/40 group-hover:bg-cyan/[0.05] transition-colors">
                                <Icon className="w-5 h-5 text-white/80 group-hover:text-cyan transition-colors" strokeWidth={1.5} />
                            </div>
                            <div className="relative mt-10 font-cabinet font-bold text-white text-2xl md:text-3xl tracking-tight leading-tight">
                                {it.t}
                            </div>
                            <div className="relative mt-4 font-plex text-[15px] md:text-base text-white/75 leading-relaxed max-w-sm">
                                {it.d}
                            </div>
                        </motion.div>
                    </RevealItem>
                    );
                })}
            </RevealStagger>
        </div>
    </section>
    );
};

/* =============================================================== */
/*  SOLUTION                                                       */
/* =============================================================== */
const Solution = () => {
    const { t } = useTranslation();
    const cards = t("landing.solution.cards", { returnObjects: true });
    const titleRef = useRef(null);
    const paragraphRef = useRef(null);
    const cardRef = useRef(null);
    const [brainStyle, setBrainStyle] = useState({ display: "none" });

    useLayoutEffect(() => {
        const offsetInside = (el, ancestor, prop = "offsetTop") => {
            let sum = 0;
            let cur = el;
            while (cur && cur !== ancestor) {
                sum += cur[prop] || 0;
                cur = cur.offsetParent;
            }
            return sum;
        };
        const compute = () => {
            const section = document.getElementById("solution");
            const h2 = titleRef.current;
            const p = paragraphRef.current;
            const card = cardRef.current;
            if (!section || !h2 || !p || !card) return;
            if (window.matchMedia("(max-width: 1023px)").matches) {
                // Mobile + Tablet: hide the brain animation entirely (desktop-only element)
                setBrainStyle({ display: "none" });
                return;
            }
            // Positions relative to section (ignores Framer Motion transforms)
            const h2Top = offsetInside(h2, section, "offsetTop");
            const cardTop = offsetInside(card, section, "offsetTop");
            const cardLeft = offsetInside(card, section, "offsetLeft");
            const cardWidth = card.offsetWidth;
            // Source video aspect and brain-in-frame offsets (measured from source: 600x338, brain at y30-310)
            const SRC_ASPECT = 600 / 338;
            const BRAIN_TOP_FRAC = 30 / 338;
            const BRAIN_BOTTOM_FRAC = 310 / 338;
            // ~30% bigger visible brain: container matches source aspect so contain fills it
            let containerWidth = cardWidth * 2;
            let containerHeight = containerWidth / SRC_ASPECT;
            // Safety clamp: brain visible bottom must not touch the card row
            const gap = 12;
            const maxContainerHeight = (cardTop - gap - h2Top) / (BRAIN_BOTTOM_FRAC - BRAIN_TOP_FRAC);
            if (containerHeight > maxContainerHeight) {
                containerHeight = maxContainerHeight;
                containerWidth = containerHeight * SRC_ASPECT;
            }
            const top = h2Top - BRAIN_TOP_FRAC * containerHeight;
            const centerX = cardLeft + cardWidth / 2;
            const left = centerX - containerWidth / 2;
            setBrainStyle({
                top: `${top}px`,
                left: `${left}px`,
                width: `${containerWidth}px`,
                height: `${containerHeight}px`,
            });
        };
        compute();
        const timers = [80, 240, 500, 900, 1400].map((ms) => setTimeout(compute, ms));
        const ro = new ResizeObserver(compute);
        const section = document.getElementById("solution");
        if (section) ro.observe(section);
        window.addEventListener("resize", compute);
        return () => {
            timers.forEach(clearTimeout);
            ro.disconnect();
            window.removeEventListener("resize", compute);
        };
    }, []);

    return (
    <section
        id="solution"
        data-testid="solution-section"
        className="relative section-y border-t border-white/[0.06] overflow-hidden"
    >
        <AmbientBackground variant="streams" opacity={0.7} />
        <ParallaxGlow className="right-[-20%] top-20" color="violet" size={700} intensity={0.6} parallaxStrength={60} />

        {/* Brain animation — top edge aligns with title top, bottom edge with paragraph bottom */}
        <div
            aria-hidden="true"
            className="pointer-events-none absolute z-[1]"
            style={brainStyle}
        >
            <video
                className="w-full h-full object-contain"
                style={{ mixBlendMode: "screen" }}
                src="/media/brain-rotation-alpha.webm"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
            />
        </div>

        <div className="relative z-10 container-x max-w-[1400px] mx-auto">
            <Reveal className="max-w-3xl">
                <h2 ref={titleRef} className="h-display text-4xl md:text-5xl lg:text-[72px] text-white leading-[0.95]">
                    {t("landing.solution.title_pre")}{" "}
                    <span className="text-gradient-accent">{t("landing.solution.title_accent")}</span>
                </h2>
                <p ref={paragraphRef} className="mt-8 font-plex text-lg text-white/70 max-w-2xl leading-relaxed">
                    {t("landing.solution.intro")}
                </p>
            </Reveal>

            <RevealStagger className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6" gap={0.12} initialDelay={0.1}>
            {[
                    { ...cards[0], i: LayersIcon },
                    { ...cards[1], i: LineChart },
                    { ...cards[2], i: BrainCircuit },
                ].map((c, idx) => (
                    <RevealItem
                        key={c.t}
                        className={`relative md:col-span-4 ${idx === 1 ? "md:translate-y-6" : ""}`}
                    >
                        <motion.div
                            ref={idx === 2 ? cardRef : undefined}
                            whileHover={{ y: -4 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="group relative h-full border border-white/[0.08] bg-graphite p-8 md:p-10 overflow-hidden"
                        >
                            <motion.div
                                aria-hidden="true"
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                style={{
                                    background: `radial-gradient(ellipse at ${idx === 0 ? "top left" : idx === 1 ? "top" : "top right"}, rgba(0,229,255,0.10), transparent 65%)`,
                                }}
                            />
                            <motion.span
                                aria-hidden="true"
                                initial={{ scaleX: 0 }}
                                whileInView={{ scaleX: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + idx * 0.1 }}
                                className="absolute left-0 top-0 h-px w-full origin-left bg-gradient-to-r from-cyan/60 via-cyan/30 to-transparent"
                            />
                            <div className="relative inline-flex items-center justify-center w-11 h-11 border border-white/[0.1] bg-white/[0.02] group-hover:border-cyan/40 group-hover:bg-cyan/[0.05] transition-colors">
                                <c.i className="w-5 h-5 text-white/80 group-hover:text-cyan transition-colors" strokeWidth={1.5} />
                            </div>
                            <div className="relative mt-10 font-cabinet font-bold text-white text-2xl md:text-3xl tracking-tight leading-tight">
                                {c.t}
                            </div>
                            <div className="relative mt-4 text-[15px] md:text-base text-white/75 font-plex leading-relaxed">
                                {c.d}
                            </div>
                        </motion.div>
                    </RevealItem>
                ))}
            </RevealStagger>
        </div>
    </section>
    );
};

/* =============================================================== */
/*  CORE INTELLIGENCE LAYERS — interactive showcase                */
/* =============================================================== */
const Layers = () => {
    const { t } = useTranslation();
    return (
    <section
        id="layers"
        data-testid="layers-section"
        className="relative section-y border-t border-white/[0.06] overflow-hidden"
    >
        <AmbientBackground variant="orbs" opacity={0.7} />

        <div className="relative container-x max-w-[1400px] mx-auto">
            <div className="mb-14 md:mb-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end">
                <Reveal className="md:col-span-7">
                    <h2 className="h-display text-4xl md:text-5xl lg:text-[64px] text-white max-w-[18ch] leading-[0.95]">
                        <span className="text-gradient-soft">{t("landing.layersHead.l1")}</span>
                        <br />
                        <span className="text-gradient-accent">{t("landing.layersHead.l2")}</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.15} className="md:col-span-5">
                    <p className="font-plex text-white/80 text-lg md:text-[19px] leading-[1.55] max-w-[46ch]">
                        {t("landing.layersHead.intro")}
                    </p>
                </Reveal>
            </div>

            <Reveal delay={0.2} y={32}>
                <LayerShowcase />
            </Reveal>
        </div>
    </section>
    );
};

/* =============================================================== */
/*  PLATFORM EXPERIENCE                                            */
/* =============================================================== */
const Platform = ({ onNavigate }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.35 });
    const { t } = useTranslation();
    const riskLabels = t("landing.platform.risk", { returnObjects: true });
    const agents = t("landing.platform.agents", { returnObjects: true });
    const cards = t("landing.platform.cards", { returnObjects: true });
    return (
        <section
            id="platform"
            data-testid="platform-section"
            className="relative section-y border-t border-white/[0.06] overflow-hidden"
        >
            <ParallaxGlow className="-left-40 top-32" color="cyan" size={540} intensity={0.55} parallaxStrength={50} />

            <div className="relative container-x max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-5 lg:sticky lg:top-32">
                    <Reveal>
                        <h2 className="h-display text-4xl md:text-5xl lg:text-[64px] text-white leading-[0.95]">
                            {t("landing.platform.l1")}<br />{t("landing.platform.l2")}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-8 font-plex text-white/80 text-lg md:text-[19px] leading-[1.55] max-w-[46ch]">
                            {t("landing.platform.intro")}
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <button
                            onClick={() => onNavigate("roadmap")}
                            data-testid="platform-preview-button"
                            className="group mt-10 inline-flex items-center gap-2 text-white font-plex text-sm border-b border-white/20 hover:border-white/60 pb-1 transition-colors"
                        >
                            {t("landing.platform.cta")}
                            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.8} />
                        </button>
                    </Reveal>
                </div>

                <Reveal delay={0.15} y={40} className="lg:col-span-7">
                    <div ref={ref} className="relative border border-white/[0.08] bg-graphite overflow-hidden">
                        {/* ambient glow inside mock */}
                        <motion.div
                            className="absolute inset-0 pointer-events-none"
                            animate={{ opacity: [0.15, 0.4, 0.15] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                            style={{
                                background:
                                    "radial-gradient(ellipse at 20% 20%, rgba(110,60,255,0.25) 0%, transparent 60%)",
                            }}
                        />
                        <div className="relative flex items-center justify-between px-5 h-10 border-b border-white/[0.06] bg-black/40">
                            <div className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
                                <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
                                <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
                            </div>
                            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                aima://workspace/alpha
                            </span>
                            <span className="font-mono text-[10px] uppercase tracking-widest text-cyan/80 flex items-center gap-1.5">
                                <motion.span
                                    animate={{ opacity: [0.4, 1, 0.4] }}
                                    transition={{ duration: 1.6, repeat: Infinity }}
                                    className="w-1.5 h-1.5 rounded-full bg-cyan"
                                />
                                live
                            </span>
                        </div>
                        <div className="relative grid grid-cols-12 gap-px bg-white/[0.06]">
                            <div className="col-span-12 md:col-span-8 bg-graphite p-6 min-h-[280px] relative">
                                <div className="flex items-center justify-between">
                                    <div className="eyebrow">{t("landing.platform.signal")}</div>
                                    <span className="font-mono text-[10px] text-white/40">conf. 0.71</span>
                                </div>
                                <div className="mt-4 font-cabinet text-4xl md:text-5xl text-white tracking-tight">
                                    +2.4% <span className="text-white/30 text-xl">↗</span>
                                </div>
                                <div className="mt-1 font-plex text-white/50 text-sm">
                                    {t("landing.platform.drift")}
                                </div>
                                <svg viewBox="0 0 400 100" className="mt-8 w-full h-24">
                                    <defs>
                                        <linearGradient id="sig" x1="0" x2="1">
                                            <stop offset="0%" stopColor="#6E3CFF" />
                                            <stop offset="100%" stopColor="#00E5FF" />
                                        </linearGradient>
                                        <linearGradient id="sig-fill" x1="0" x2="0" y1="0" y2="1">
                                            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.35" />
                                            <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
                                        </linearGradient>
                                    </defs>
                                    <motion.path
                                        d="M0 70 L40 60 L80 68 L120 50 L160 55 L200 40 L240 45 L280 30 L320 36 L360 22 L400 28 L400 100 L0 100 Z"
                                        fill="url(#sig-fill)"
                                        initial={{ opacity: 0 }}
                                        animate={inView ? { opacity: 1 } : {}}
                                        transition={{ duration: 1.4, delay: 0.8 }}
                                    />
                                    <motion.path
                                        d="M0 70 L40 60 L80 68 L120 50 L160 55 L200 40 L240 45 L280 30 L320 36 L360 22 L400 28"
                                        stroke="url(#sig)"
                                        strokeWidth="1.5"
                                        fill="none"
                                        initial={{ pathLength: 0, opacity: 0 }}
                                        animate={inView ? { pathLength: 1, opacity: 1 } : {}}
                                        transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
                                    />
                                    <motion.path
                                        d="M0 80 L40 75 L80 78 L120 70 L160 72 L200 65 L240 68 L280 58 L320 60 L360 52 L400 55"
                                        stroke="rgba(255,255,255,0.18)"
                                        strokeWidth="1"
                                        fill="none"
                                        strokeDasharray="2 3"
                                        initial={{ pathLength: 0 }}
                                        animate={inView ? { pathLength: 1 } : {}}
                                        transition={{ duration: 2.4, delay: 0.3 }}
                                    />
                                </svg>
                            </div>
                            <div className="col-span-12 md:col-span-4 bg-graphite p-6 min-h-[280px]">
                                <div className="eyebrow mb-4">{t("landing.platform.risk_view")}</div>
                                {[
                                    { k: riskLabels[0], v: 0.22, c: "bg-cyan" },
                                    { k: riskLabels[1], v: 0.48, c: "bg-violet" },
                                    { k: riskLabels[2], v: 0.77, c: "bg-white" },
                                    { k: riskLabels[3], v: 0.35, c: "bg-cyan" },
                                ].map((r, i) => (
                                    <div key={r.k} className="mb-4">
                                        <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-white/50">
                                            <span>{r.k}</span>
                                            <span>{r.v.toFixed(2)}</span>
                                        </div>
                                        <div className="mt-1.5 h-[3px] bg-white/[0.06] overflow-hidden">
                                            <motion.div
                                                className={`h-full ${r.c}`}
                                                initial={{ width: 0 }}
                                                animate={inView ? { width: `${r.v * 100}%` } : {}}
                                                transition={{ duration: 1.2, delay: 0.6 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="col-span-12 md:col-span-5 bg-graphite p-6">
                                <div className="eyebrow mb-3">{t("landing.platform.airoom")}</div>
                                <ul className="space-y-3 text-[13px] font-plex text-white/70">
                                    {[
                                        { color: "text-cyan", fill: "fill-cyan", name: agents[0].n, body: agents[0].b },
                                        { color: "text-violet", fill: "fill-violet", name: agents[1].n, body: agents[1].b },
                                        { color: "text-white", fill: "fill-white", name: agents[2].n, body: agents[2].b },
                                        { color: "text-cyan", fill: "fill-cyan", name: agents[3].n, body: agents[3].b },
                                    ].map((a, i) => (
                                        <motion.li
                                            key={a.name}
                                            initial={{ opacity: 0, x: -8 }}
                                            animate={inView ? { opacity: 1, x: 0 } : {}}
                                            transition={{ duration: 0.6, delay: 1 + i * 0.15 }}
                                            className="flex gap-2"
                                        >
                                            <CircleDot className={`w-3 h-3 mt-[4px] ${a.color}`} strokeWidth={2} />
                                            <span>
                                                <span className="text-white">{a.name}</span> — {a.body}
                                            </span>
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>
                            <div className="col-span-12 md:col-span-7 bg-graphite p-6 relative">
                                <div className="eyebrow mb-3">{t("landing.platform.explain")}</div>
                                <p className="font-plex text-[13.5px] text-white/70 leading-relaxed">
                                    {t("landing.platform.explain_body")}
                                </p>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {["forecast", "structure", "sentiment", "risk", "liquidity"].map((t, i) => (
                                        <motion.span
                                            key={t}
                                            initial={{ opacity: 0, y: 4 }}
                                            animate={inView ? { opacity: 1, y: 0 } : {}}
                                            transition={{ duration: 0.45, delay: 1.4 + i * 0.07 }}
                                            className="font-mono text-[10px] uppercase tracking-widest text-white/60 border border-white/10 px-2 py-1"
                                        >
                                            {t}
                                        </motion.span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <RevealStagger className="mt-6 grid grid-cols-3 gap-px bg-white/[0.06]" gap={0.1}>
                        {cards.map((it) => (
                            <RevealItem key={it.t}>
                                <div className="bg-graphite p-5 h-full">
                                    <div className="eyebrow">{it.t}</div>
                                    <div className="mt-2 text-white font-cabinet tracking-tight">
                                        {it.d}
                                    </div>
                                </div>
                            </RevealItem>
                        ))}
                    </RevealStagger>
                </Reveal>
            </div>
        </section>
    );
};

/* =============================================================== */
/*  WHY DIFFERENT                                                  */
/* =============================================================== */
const WhyDifferent = () => {
    const { t } = useTranslation();
    const data = t("landing.why.pillars", { returnObjects: true });
    const icons = [Network, BrainCircuit, LayersIcon, Activity];
    const pillars = data.map((p, i) => ({ ...p, i: icons[i] }));
    return (
        <section
            id="why"
            data-testid="why-section"
            className="relative section-y border-t border-white/[0.06] overflow-hidden"
        >
            <AmbientBackground variant="dots" opacity={0.85} />
            <div className="relative container-x max-w-[1400px] mx-auto">
                <Reveal className="max-w-3xl mb-16">
                    <h2 className="h-display text-4xl md:text-5xl lg:text-[72px] text-white leading-[0.95]">
                        {t("landing.why.l1")}<br />{t("landing.why.l2")}
                    </h2>
                </Reveal>

                <RevealStagger gap={0.09} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {pillars.map((p, i) => (
                        <RevealItem key={p.t}>
                            <motion.div
                                whileHover={{ y: -4 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                className="group relative bg-graphite border border-white/[0.08] p-10 md:p-12 h-full overflow-hidden"
                            >
                                <motion.div
                                    aria-hidden="true"
                                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                    style={{
                                        background:
                                            i % 2 === 0
                                                ? "radial-gradient(ellipse at top left, rgba(0,229,255,0.10), transparent 60%)"
                                                : "radial-gradient(ellipse at top right, rgba(110,60,255,0.10), transparent 60%)",
                                    }}
                                />
                                <motion.span
                                    aria-hidden="true"
                                    initial={{ scaleX: 0 }}
                                    whileInView={{ scaleX: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.08 }}
                                    className={`absolute left-0 top-0 h-px w-full origin-left bg-gradient-to-r ${i % 2 === 0 ? "from-cyan/60 via-cyan/30 to-transparent" : "from-violet/60 via-violet/30 to-transparent"}`}
                                />
                                <div className="relative inline-flex items-center justify-center w-11 h-11 border border-white/[0.1] bg-white/[0.02] group-hover:border-cyan/40 group-hover:bg-cyan/[0.05] transition-colors">
                                    <p.i className="w-5 h-5 text-white/80 group-hover:text-cyan transition-colors" strokeWidth={1.5} />
                                </div>
                                <div className="relative mt-10 font-cabinet font-bold text-white text-2xl md:text-3xl tracking-tight leading-tight">
                                    {p.t}
                                </div>
                                <div className="relative mt-4 text-[15px] md:text-base text-white/75 font-plex leading-relaxed max-w-md">
                                    {p.d}
                                </div>
                            </motion.div>
                        </RevealItem>
                    ))}
                </RevealStagger>
            </div>
        </section>
    );
};

/* =============================================================== */
/*  ROADMAP — 12mo / 24mo / 5yr switcher                           */
/* =============================================================== */
const RoadmapTabs = ({ active, onChange }) => {
    const { t } = useTranslation();
    const keys = ["12m", "24m", "5y"];
    return (
        <div
            role="tablist"
            aria-label="Roadmap horizon"
            className="inline-flex items-center p-1 border border-white/[0.08] bg-graphite"
            data-testid="roadmap-tabs"
        >
            {keys.map((k) => {
                const isActive = active === k;
                return (
                    <button
                        key={k}
                        role="tab"
                        aria-selected={isActive}
                        data-testid={`roadmap-tab-${k}`}
                        onClick={() => onChange(k)}
                        className={`relative px-5 md:px-6 h-11 font-mono text-[12px] uppercase tracking-[0.2em] font-semibold transition-colors ${
                            isActive ? "text-cyan" : "text-white/70 hover:text-white"
                        }`}
                    >
                        {isActive && (
                            <motion.span
                                layoutId="roadmap-tab-indicator"
                                transition={{ type: "spring", stiffness: 260, damping: 30 }}
                                className="absolute inset-0 bg-cyan/[0.06] border border-cyan/40"
                            />
                        )}
                        <span className="relative z-10">{t(`landing.roadmap.${k}.label`)}</span>
                    </button>
                );
            })}
        </div>
    );
};

const RoadmapTimeline = ({ view }) => {
    const { t } = useTranslation();
    const data = t(`landing.roadmap.${view}`, { returnObjects: true });
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 0.8", "end 0.2"],
    });
    const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

    return (
        <motion.div
            key={view}
            initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
        >
            <div ref={ref} className="relative ml-2 md:ml-4">
                <div className="absolute left-0 top-0 bottom-0 w-px bg-white/[0.08]" />
                <motion.div
                    style={{ height: lineHeight }}
                    className="absolute left-0 top-0 w-px bg-gradient-to-b from-cyan via-violet to-cyan/0"
                />
                <ol className="relative">
                    {data.items.map((it, idx) => {
                        const dot =
                            it.state === "done"
                                ? "bg-white"
                                : it.state === "active"
                                ? "bg-cyan shadow-[0_0_0_4px_rgba(0,229,255,0.15)]"
                                : "bg-white/20";
                        const stateLabel =
                            it.state === "active"
                                ? t("landing.roadmapHead.state_active")
                                : it.state === "done"
                                ? t("landing.roadmapHead.state_done")
                                : t("landing.roadmapHead.state_future");
                        return (
                            <Reveal
                                as="li"
                                y={20}
                                delay={idx * 0.06}
                                key={`${view}-${it.p}`}
                                className="relative pl-10 md:pl-14 pb-14 last:pb-0"
                            >
                                <span
                                    data-testid={`roadmap-${view}-${it.p.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`absolute -left-[7px] top-[10px] h-3.5 w-3.5 rounded-full ${dot}`}
                                />
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                                    <div className="md:col-span-3">
                                        <div className="font-mono text-[13px] uppercase tracking-[0.22em] text-white font-semibold">
                                            {it.p}
                                        </div>
                                        <div className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white/50">
                                            {it.range}
                                        </div>
                                        <div className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">
                                            <span className={`w-1 h-1 rounded-full ${it.state === "active" ? "bg-cyan" : it.state === "done" ? "bg-white" : "bg-white/30"}`} />
                                            {stateLabel}
                                        </div>
                                    </div>
                                    <div className="md:col-span-9">
                                        <div className="font-cabinet font-bold text-cyan text-2xl md:text-3xl tracking-tight leading-tight">
                                            {it.t}
                                        </div>
                                        <div className="mt-2 font-plex text-white/75 text-[15px] max-w-2xl leading-relaxed">
                                            {it.d}
                                        </div>
                                        {it.bullets && it.bullets.length > 0 && (
                                            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 max-w-2xl">
                                                {it.bullets.map((b) => (
                                                    <li
                                                        key={b}
                                                        className="flex gap-2.5 items-start font-plex text-[14px] text-white/70 leading-relaxed"
                                                    >
                                                        <span className="mt-[8px] h-px w-3 bg-cyan/60 shrink-0" />
                                                        <span>{b}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </ol>
            </div>
        </motion.div>
    );
};

const RoadmapAnchorStrip = ({ active, onChange }) => {
    const { t } = useTranslation();
    const views = ["12m", "24m", "5y"];
    const anchors = t("landing.roadmap.anchors", { returnObjects: true }).map((a, i) => ({ ...a, view: views[i] }));
    return (
    <RevealStagger gap={0.08} className="mb-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06]">
        {anchors.map((a) => {
            const isActive = active === a.view;
            return (
                <RevealItem key={a.view}>
                    <button
                        type="button"
                        onClick={() => onChange(a.view)}
                        data-testid={`roadmap-anchor-${a.view}`}
                        aria-pressed={isActive}
                        className={`group relative w-full text-left p-6 md:p-7 h-full transition-colors duration-300 ${
                            isActive ? "bg-[#0f0f12]" : "bg-graphite hover:bg-[#0d0d10]"
                        }`}
                    >
                        {/* Active accent bar */}
                        <motion.span
                            initial={false}
                            animate={{ scaleX: isActive ? 1 : 0 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute left-0 top-0 h-px w-full origin-left bg-gradient-to-r from-cyan via-violet to-transparent"
                        />
                        <div className="flex items-center justify-between">
                            <span className={`font-mono text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                                isActive ? "text-cyan" : "text-white/75 group-hover:text-white"
                            }`}>{a.eyebrow}</span>
                            <span
                                className={`relative flex h-1.5 w-1.5 ${
                                    isActive ? "" : "opacity-40"
                                }`}
                            >
                                {isActive && (
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-cyan/70 animate-pulse-soft" />
                                )}
                                <span
                                    className={`relative inline-flex h-1.5 w-1.5 rounded-full ${
                                        isActive ? "bg-cyan" : "bg-white/30"
                                    }`}
                                />
                            </span>
                        </div>
                        <div
                            className={`mt-7 font-cabinet font-bold text-2xl md:text-[26px] tracking-tight leading-tight transition-colors ${
                                isActive ? "text-white" : "text-white/90 group-hover:text-white"
                            }`}
                        >
                            {a.milestone}
                        </div>
                        <div className="mt-2 font-plex text-[14.5px] text-white/70 leading-relaxed max-w-xs">
                            {a.sub}
                        </div>
                        <div className="mt-6 flex items-center justify-between">
                            <span className={`font-mono text-[11px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                                isActive ? "text-cyan/90" : "text-white/60"
                            }`}>
                                {a.meta}
                            </span>
                            <span
                                className={`font-mono text-[11px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                                    isActive ? "text-cyan" : "text-white/55 group-hover:text-white/85"
                                }`}
                            >
                                {isActive ? t("landing.roadmapHead.viewing") : t("landing.roadmapHead.view")}
                            </span>
                        </div>
                    </button>
                </RevealItem>
            );
        })}
    </RevealStagger>
    );
};

const Roadmap = () => {
    const { t } = useTranslation();
    const [view, setView] = useState("12m");
    const data = t(`landing.roadmap.${view}`, { returnObjects: true });

    return (
        <section
            id="roadmap"
            data-testid="roadmap-section"
            className="relative section-y border-t border-white/[0.06] overflow-hidden"
        >
            <AmbientBackground variant="streams" opacity={0.6} />
            <div className="relative container-x max-w-[1400px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end mb-12">
                    <Reveal className="md:col-span-7">
                        <h2 className="h-display text-4xl md:text-5xl lg:text-[64px] text-white max-w-[20ch] leading-[0.95]">
                            {t("landing.roadmapHead.l1")}<br />{t("landing.roadmapHead.l2")}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="md:col-span-5">
                        <p className="font-plex text-white/80 text-lg md:text-[19px] leading-[1.55] max-w-[46ch]">
                            {t("landing.roadmapHead.intro")}
                        </p>
                    </Reveal>
                </div>

                <RoadmapAnchorStrip active={view} onChange={setView} />

                <Reveal delay={0.2} className="mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                    <div>
                        <div className="font-cabinet font-bold text-white text-2xl md:text-[28px] tracking-tight leading-tight">
                            {data.subtitle}
                        </div>
                        <div className="mt-1.5 font-mono uppercase text-cyan text-[11.5px] tracking-[0.22em] font-semibold">
                            {t("landing.roadmapHead.execution_label")}
                        </div>
                    </div>
                    <RoadmapTabs active={view} onChange={setView} />
                </Reveal>

                <RoadmapTimeline view={view} />
            </div>
        </section>
    );
};

/* =============================================================== */
/*  TEAM                                                           */
/* =============================================================== */
const Team = () => {
    const { t } = useTranslation();
    return (
    <section
        id="team"
        data-testid="team-section"
        className="relative section-y border-t border-white/[0.06] overflow-hidden"
    >
        <AmbientBackground variant="orbs" opacity={0.55} />
        <div className="relative container-x max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end mb-12 md:mb-16">
                <Reveal className="md:col-span-7">
                    <h2 className="h-display text-4xl md:text-5xl lg:text-[72px] text-white leading-[0.95]">
                        <span className="text-gradient-soft">{t("landing.team.l1")}</span>
                        <br />
                        <span className="text-gradient-accent">{t("landing.team.l2")}</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.1} className="md:col-span-5">
                    <p className="font-plex text-white/80 text-lg md:text-[19px] leading-[1.55] max-w-[46ch]">
                        {t("landing.team.intro")}
                    </p>
                </Reveal>
            </div>

            <Reveal y={28} delay={0.15}>
                <TeamShowcase />
            </Reveal>

            <Reveal delay={0.25} className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-4 border-t border-white/[0.06] pt-6">
                <div className="font-mono text-[10.5px] uppercase tracking-[0.28em] text-white/45">
                    {t("landing.team.disclosed")}
                </div>
                <a
                    href="mailto:contact@aima.market"
                    data-testid="team-contact-link"
                    className="font-mono text-[10.5px] uppercase tracking-[0.28em] text-white/55 hover:text-white transition-colors"
                >
                    {t("landing.team.enquiries")}
                </a>
            </Reveal>
        </div>
    </section>
    );
};

/* =============================================================== */
/*  FINAL CTA                                                      */
/* =============================================================== */
const FinalCTA = () => {
    const { t } = useTranslation();
    return (
    <section
        id="cta"
        data-testid="final-cta-section"
        className="relative section-y border-t border-white/[0.06] overflow-hidden"
    >
        <div className="absolute inset-0 grid-lines-bg opacity-40 [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_30%,transparent_70%)]" />
        <ParallaxGlow className="left-1/2 -translate-x-1/2 top-16" color="violet" size={900} intensity={0.75} parallaxStrength={30} />
        <ParallaxGlow className="left-1/2 -translate-x-1/2 top-40" color="cyan" size={600} intensity={0.5} parallaxStrength={20} />

        <div className="relative container-x max-w-[1400px] mx-auto text-center">
            <Reveal>
                <h2 className="h-display text-5xl md:text-7xl lg:text-[128px] text-white mx-auto max-w-[16ch] leading-[0.9]">
                    <span className="text-gradient-soft">{t("landing.finalcta.l1")}</span>
                    <br />
                    <span className="text-gradient-accent">{t("landing.finalcta.l2")}</span>
                </h2>
            </Reveal>
            <Reveal delay={0.25}>
                <p className="mt-10 font-plex text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
                    {t("landing.finalcta.intro")}
                </p>
            </Reveal>
            <Reveal delay={0.4}>
                <div className="mt-12 inline-flex flex-col sm:flex-row items-center gap-4">
                    <a
                        href="mailto:contact@aima.market"
                        data-testid="final-contact-button"
                        className="group inline-flex items-center gap-3 h-14 px-8 bg-white text-black font-plex text-sm font-medium hover:bg-white/90 transition-colors"
                    >
                        {t("landing.finalcta.contact")}
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.7} />
                    </a>
                    <WhitepaperButton testId="final-whitepaper-label" size="lg" />
                </div>
            </Reveal>
        </div>
    </section>
    );
};

/* =============================================================== */
/*  FOOTER                                                         */
/* =============================================================== */
export const Footer = () => {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || "en").slice(0, 2);
    const legal = (slug) => `/${lang}/${slug}`;
    const legalTitle = (slug) => getLegalPage(lang, slug)?.title || slug;
    return (
    <footer data-testid="site-footer" className="relative border-t border-white/[0.06]">
        <div className="container-x max-w-[1400px] mx-auto py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div data-testid="footer-brand" className="md:col-span-4 flex flex-col items-center md:items-center text-center md:text-center">
                <AimaLogo variant="stacked" height={180} />
                <div className="mt-8 font-cabinet font-bold text-white text-xl md:text-[22px] tracking-[0.02em] leading-[1.15] max-w-sm">
                    AI Market Intelligence Assistant
                </div>
                <p className="mt-4 font-plex text-white/70 text-[15px] max-w-sm leading-[1.65]">
                    {t("landing.footer.tagline")}
                </p>
            </div>
            <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8" data-testid="footer-nav-grid">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/70 font-semibold mb-5">{t("landing.footer.col_platform")}</div>
                    <ul className="space-y-2.5 font-plex text-[14px] text-white/75">
                        <li><a href="#platform" className="hover:text-white transition-colors">{t("landing.footer.l_platform")}</a></li>
                        <li><a href="#layers" className="hover:text-white transition-colors">{t("landing.footer.l_intelligence")}</a></li>
                        <li><a href="#why" className="hover:text-white transition-colors">{t("landing.footer.l_architecture")}</a></li>
                        <li><a href="#roadmap" className="hover:text-white transition-colors">AIMA Marketplace</a></li>
                        <li><a href="#platform" className="hover:text-white transition-colors">AI Rooms</a></li>
                        <li><a href="#platform" className="hover:text-white transition-colors">Strategy Composer</a></li>
                    </ul>
                </div>
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/70 font-semibold mb-5">{t("landing.footer.col_company")}</div>
                    <ul className="space-y-2.5 font-plex text-[14px] text-white/75">
                        <li><a href="#roadmap" className="hover:text-white transition-colors">Roadmap</a></li>
                        <li><a href="#team" className="hover:text-white transition-colors">{t("landing.footer.l_team")}</a></li>
                        <li><WhitepaperLink /></li>
                        <li><a href="#token-sale" className="hover:text-white transition-colors">Tokenomics</a></li>
                        <li><a href="#token-sale" className="hover:text-white transition-colors">Private Sale</a></li>
                        <li><a href="#token-sale" className="hover:text-white transition-colors">{t("landing.footer.l_waitlist")}</a></li>
                    </ul>
                </div>
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/70 font-semibold mb-5">{t("landing.footer.col_legal")}</div>
                    <ul className="space-y-2.5 font-plex text-[14px] text-white/75">
                        <li><Link to={legal("risk-disclaimer")} data-testid="footer-legal-risk-disclaimer" className="hover:text-white transition-colors">{legalTitle("risk-disclaimer")}</Link></li>
                        <li><Link to={legal("terms-of-use")} data-testid="footer-legal-terms-of-use" className="hover:text-white transition-colors">{legalTitle("terms-of-use")}</Link></li>
                        <li><Link to={legal("privacy-policy")} data-testid="footer-legal-privacy-policy" className="hover:text-white transition-colors">{legalTitle("privacy-policy")}</Link></li>
                        <li><Link to={legal("private-sale-notice")} data-testid="footer-legal-private-sale-notice" className="hover:text-white transition-colors">{legalTitle("private-sale-notice")}</Link></li>
                        <li><Link to={legal("cookie-notice")} data-testid="footer-legal-cookie-notice" className="hover:text-white transition-colors">{legalTitle("cookie-notice")}</Link></li>
                        <li><a href="mailto:contact@aima.market" className="hover:text-white transition-colors">{t("landing.footer.l_contact")}</a></li>
                    </ul>
                </div>
                {/* Community block — 4th cell on mobile grid, hidden on desktop where SocialLinks lives below */}
                <div data-testid="footer-community-mobile" className="md:hidden">
                    <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/70 font-semibold mb-5">Community</div>
                    <div className="pt-1" data-testid="footer-community-icons">
                        <SocialLinks align="start" hideHeading />
                    </div>
                </div>
            </div>
        </div>

        {/* Compact legal disclaimer + treasury warning + social row (desktop) */}
        <div className="border-t border-white/[0.06]">
            <div className="container-x max-w-[1400px] mx-auto py-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                <div className="space-y-4 md:flex-1 md:max-w-[70%]">
                    <p data-testid="footer-legal-disclaimer" className="font-plex text-[11.5px] md:text-[11.5px] text-white/40 leading-relaxed">
                        {t("landing.footer.disclaimer")}
                    </p>
                    <p data-testid="footer-treasury-warning" className="font-plex text-[11.5px] md:text-[11.5px] text-white/60 leading-relaxed border-l-2 border-cyan/40 pl-4">
                        {t("landing.footer.treasury_warning")}
                    </p>
                </div>
                <div className="hidden md:block md:shrink-0" data-testid="footer-social-desktop">
                    <SocialLinks align="end" hideHeading />
                </div>
            </div>
        </div>

        <div className="border-t border-white/[0.06]" data-testid="footer-tags-row">
            <div className="container-x max-w-[1400px] mx-auto py-4 flex justify-center sm:justify-end">
                <div className="footer-neon font-mono text-[10.5px] uppercase tracking-[0.22em] text-center" data-testid="footer-tags">
                    {t("landing.footer.tags")}
                </div>
            </div>
        </div>

        <div className="border-t border-white/[0.06]" data-testid="footer-status-row">
            <div className="container-x max-w-[1400px] mx-auto py-6 flex justify-center sm:justify-start">
                <div className="footer-neon font-mono text-[10.5px] uppercase tracking-[0.22em] text-center sm:text-left" data-testid="footer-status">
                    © 2026 AIMA · {t("landing.footer.rights")} · {t("landing.footer.sale_status")}
                </div>
            </div>
        </div>
    </footer>
    );
};

/* =============================================================== */
/*  PAGE                                                           */
/* =============================================================== */
const Landing = () => {
    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
            <SmoothScroll />
            <CinematicVignette />
            <Nav onNavigate={scrollTo} />
            <main>
                <Hero onNavigate={scrollTo} />
                <MarketTicker />
                <VisionSection />
                <Problem />
                <Solution />
                <Layers />
                <Platform onNavigate={scrollTo} />
                <WhyDifferent />
                <Roadmap />
                <RestrictedTokenSale />
                <Team />
                <FinalCTA />
            </main>
            <Footer />
        </div>
    );
};

export default Landing;
